/**
 * #19979 — luật cắt khung giờ ở chế độ "Giờ cụ thể", dùng để XEM TRƯỚC trên trang Khung giờ nhận
 * lịch. Phải khớp `AvailabilityService` (nhánh SLOT): khung bắt đầu từ giờ mở của từng khoảng,
 * bước N phút, và chỉ giữ khung nếu `m + N <= end` — phần dư cuối khoảng bị BỎ. Lệch luật ở đây là
 * màn hình hứa một số khung mà bệnh nhân không thấy (cùng loại lỗi #19880/#19881), nên các ví dụ
 * được ghim bằng test.
 */

/** Các độ dài khung cho chọn. Giá trị cũ ngoài danh sách vẫn được hiện (không tự đổi). */
export const SLOT_MINUTE_OPTIONS = [10, 15, 20, 30, 45, 60] as const;

function toMin(hhmm: string): number | null {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

function toHhmm(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Giờ bắt đầu của mọi khung trong khoảng [start, end), bước {@code minutes}. */
export function slotStarts(start: string, end: string, minutes: number): string[] {
  const s = toMin(start);
  const e = toMin(end);
  if (s === null || e === null || !(minutes > 0)) return [];
  const out: string[] = [];
  for (let m = s; m + minutes <= e; m += minutes) out.push(toHhmm(m));
  return out;
}

/** Số khung của một khoảng. */
export function slotCount(start: string, end: string, minutes: number): number {
  return slotStarts(start, end, minutes).length;
}

/** Phần dư cuối khoảng không thành khung được; null khi chia hết (hoặc giờ không hợp lệ). */
export function slotLeftover(
  start: string,
  end: string,
  minutes: number,
): { from: string; to: string } | null {
  const s = toMin(start);
  const e = toMin(end);
  if (s === null || e === null || !(minutes > 0) || e <= s) return null;
  const used = Math.floor((e - s) / minutes) * minutes;
  if (s + used === e) return null;
  return { from: toHhmm(s + used), to: end };
}

/**
 * Mã buổi sinh từ tên: bỏ dấu, viết hoa, ký tự khác chữ/số thành "_", tối đa 16 ký tự (server
 * `@Size(max = 16)`), và không trùng mã đã có (thêm hậu tố _2, _3…). Mã không đổi được sau khi
 * tạo, nên chỉ sinh cho buổi MỚI.
 */
export function sessionCodeFrom(label: string, taken: Iterable<string>): string {
  const base =
    label
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/đ/g, "d")
      .replace(/Đ/g, "D")
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 16)
      .replace(/_+$/g, "") || "BUOI";
  const used = new Set(taken);
  if (!used.has(base)) return base;
  for (let i = 2; ; i++) {
    const suffix = `_${i}`;
    const candidate = base.slice(0, 16 - suffix.length).replace(/_+$/g, "") + suffix;
    if (!used.has(candidate)) return candidate;
  }
}
